<template>
  <div class="function-link">
    <PageTitle
      title="Line串接設定"
      icon="chevron_left"
      hideBtn
      style="padding-bottom: 24px"
      @iconClick="goParameters"
    />

    <LineNewSettings
      v-if="isOhbotSuccessAdmin"
      v-featureKey="'admin.authServiceLineOauthConfig.showUseShopLineLogin'"
    />

    <section class="section-block">
      <SectionTitle
        title="Messaging API Channel 設定"
        @edit="showDialog = true"
      />

      <BaseElForm label-position="left" label-width="200px">
        <BaseElFormItem label="Line ID" prop="lineId">
          <div class="secret-text">
            <span>{{ settingData.lineId || '尚未設定' }}</span>
          </div>
        </BaseElFormItem>
        <BaseElFormItem label="Channel ID" prop="channelId">
          <div class="secret-text">
            <span>{{ settingData.channelId || '尚未設定' }}</span>
          </div>
        </BaseElFormItem>
        <BaseElFormItem label="Channel Secret" prop="channelSecret">
          <div class="secret-text">
            <span>{{ settingData.channelSecret || '尚未設定' }}</span>
          </div>
        </BaseElFormItem>
        <BaseElFormItem label="AccessToken" prop="channelAccessToken">
          <div class="secret-text">
            <span>{{ settingData.channelAccessToken || '尚未設定' }}</span>
          </div>
        </BaseElFormItem>

        <BaseElFormItem label="Webhook URL" prop="webhookUrl">
          <div class="flex items-center text-gray-80" style="gap: 8px">
            <p>{{ lineWebhookUrl || '尚未設定' }}</p>
            <div v-if="lineWebhookUrl" class="flex items-center justify-center cursor-pointer" @click="onCopyWebhookUrl">
              <MaterialIcon size="20" color="var(--gray-80)">content_copy_outlined</MaterialIcon>
            </div>
          </div>
        </BaseElFormItem>
      </BaseElForm>
    </section>

    <LineLoginSettings />

    <LineBindingSetting />

    <section class="section-block">
      <SectionTitle title="通知訊息設定" hideBtn />
      <BaseElForm label-position="left" label-width="200px">
        <div v-for="(items, scope) in notifyList" :key="scope">
          <template v-if="showControl.notifyScope[scope]">
            <BaseElFormItem
              v-for="item in items"
              :key="item.key"
            >
              <template slot="label">
                <div class="flex items-center">
                  <span>{{ item.label }}</span>
                  <TipInfo v-if="item.tooltip" class="ml-1" :content="item.tooltip">
                    {{ item.tooltip }}
                  </TipInfo>
                </div>
              </template>
              <BaseElSwitch
                v-model="messageOption[item.key]"
                active-text="開啟通知"
                inactive-text="關閉通知"
                :disabled="item.disabled"
                @change="updateLinePushGroup($event, [item.key])"
              />
              <BaseElButton v-if="item.event" class="advance-btn" type="text" @click="item.event">進階設定</BaseElButton>
            </BaseElFormItem>
          </template>
        </div>
      </BaseElForm>
    </section>

    <LineModule />

    <!-- Dialog -->
    <el-dialog
      :close-on-click-modal="false"
      :visible.sync="showDialog"
      title="編輯 Messaging API Channel 設定"
    >
      <BaseElForm
        ref="form"
        :model="formData"
        :rules="formRules"
        label-position="top"
      >
        <BaseElFormItem label="Line ID" prop="lineId">
          <BaseElInput
            v-model="formData.lineId"
            class="dialog-input"
            placeholder="請輸入"
          />
        </BaseElFormItem>

        <BaseElFormItem label="Channel ID" prop="channelId">
          <BaseElInput
            v-model="formData.channelId"
            class="dialog-input"
            placeholder="請輸入"
          />
        </BaseElFormItem>

        <BaseElFormItem label="Channel Secret" prop="channelSecret">
          <BaseElInput
            v-model="formData.channelSecret"
            class="dialog-input"
            placeholder="請輸入"
          />
        </BaseElFormItem>

        <BaseElFormItem label="AccessToken" prop="channelAccessToken">
          <BaseElInput
            v-model="formData.channelAccessToken"
            class="dialog-input"
            placeholder="請輸入"
          />
        </BaseElFormItem>
      </BaseElForm>

      <div slot="footer">
        <BaseElButton plain @click="showDialog = false">取消</BaseElButton>
        <BaseElButton type="primary" @click="updateLineConfig">儲存</BaseElButton>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import LineBindingSetting from './components/LineBindingSetting.vue'
import LineModule from './components/LineModule.vue'
import LineLoginSettings from './components/LineLoginSettings.vue'
import LineNewSettings from './components/LineNewSettings.vue'
import PageTitle from '@/components/Title/PageTitle'
import SectionTitle from '@/components/Title/SectionTitle'
import TipInfo from '@/components/TipInfo.vue'
import { mapGetters } from 'vuex'
import { defineComponent, computed, ref } from 'vue'
import copy from 'clipboard-copy'
import {
  FindLineConfig,
  UpdateLineConfig,
  CreateLiff,
  GetLiff,
  GetLinePushConfig,
  UpdateLinePushConfig,
} from '@/api/shop'
import { noEmptyRules } from '@/validation'
import formUtils from '@/utils/form'
import { checkUserFeature } from '@/store/modules/permission'
import { usePermissions } from '@/use/permissions'
import { GetLineWebhookUrl } from '@/api/line'
import { useShop } from '@/use/shop'
import { useRouter } from 'vue-router/composables'
import store from '@/store'
import { get } from 'lodash'

export default defineComponent({
  name: 'FunctionLink',
  components: {
    PageTitle,
    SectionTitle,
    LineModule,
    LineLoginSettings,
    LineBindingSetting,
    LineNewSettings,
    TipInfo,
  },
  setup () {
    const { checkAction } = usePermissions()
    const { shopId } = useShop()
    const isOhbotSuccessAdmin = computed(() => checkAction('admin.authServiceLineOauthConfig.showUseShopLineLogin'))
    const router = useRouter()

    const isTrustModuleEnabled = computed(() => {
      return checkAction('planModule.newebpayTrustReport.enable') || checkAction('planModule.salesPaymentConfigEzAIO.enable')
    })

    const formData = ref({
      lineId: '',
      channelId: '',
      channelSecret: '',
      channelAccessToken: '',
    })

    const settingData = ref({
      lineId: '',
      channelId: '',
      channelSecret: '',
      channelAccessToken: '',
      webhookUrl: '',
    })

    const lineWebhookUrl = ref('')

    const getLineWebhookUrl = async () => {
      const [res, err] = await GetLineWebhookUrl({
        shopId: shopId.value,
      })
      if (err) {
        window.$message.error(err)
        return
      }
      lineWebhookUrl.value = res.webhookUrl
    }

    const onCopyWebhookUrl = () => {
      copy(lineWebhookUrl.value)
      window.$message.success('已成功複製 Webhook URL！')
    }
    const goAdvanceCouponRecordNotification = () => {
      router.push({ name: 'AdvanceCouponRecordNotification' })
    }
    const goAdvanceCouponRecordExpireNotification = () => {
      router.push({ name: 'AdvanceCouponRecordExpireNotification' })
    }
    const goAdvanceTicketBoxRecordOpen = () => {
      router.push({ name: 'AdvanceTicketBoxRecordOpen' })
    }
    const goAdvanceTicketBoxRecordUse = () => {
      router.push({ name: 'AdvanceTicketBoxRecordUse' })
    }
    const goAdvanceTicketBoxRecordVoided = () => {
      router.push({ name: 'AdvanceTicketBoxRecordVoided' })
    }
    const goAdvanceTicketBoxRecordExpire = () => {
      router.push({ name: 'AdvanceTicketBoxRecordExpire' })
    }
    const goParameters = () => {
      router.push({ name: 'Parameters' })
    }
    const role = computed(() => get(store.getters, 'role'))
    const userPlanFeature = computed(() => get(store.getters, 'userPlanFeature'))
    const userFeatures = computed(() => get(store.getters, 'userFeatures'))
    const checkPermission = (action) => {
      return role.value === 'ohbotSuccessAdmin' ? true : checkUserFeature(userPlanFeature.value, userFeatures.value, action)
    }
    const showControl = computed(() => {
      const controls = {
        notifyScope: {
          appointment: checkAction('planModule.appointment.enable'),
          deposit: checkAction('planModule.appointmentDeposit.enable'),
          wallet: checkAction('admin.walletRecord.page'),
          shopCashback: checkAction('planModule.shopCashback.enable'),
          coupon: checkAction('planModule.coupon.enable'),
          classTicket: checkAction('planModule.classTicket.enable'),
          allianceActivity: checkPermission('ohbot.*') && checkPermission('admin.allianceActivity.page'),
          shopPoint: checkAction('planModule.shopPoint.enable'),
          // 會員等級、推薦會員另需對應功能（GIMMATEK 皆不提供，CNCY-10766）
          memberLevel: checkAction('planModule.member.enable') && checkAction('admin.memberLevelConfig.page'),
          memberInvite: checkAction('planModule.member.enable') && checkAction('admin.memberReferralRecord.find'),
          punchCard: checkAction('planModule.punchCard.enable'),
          pointCard: checkAction('planModule.pointCard.enable'),
          periodicBenefit: checkAction('planModule.periodicBenefit.enable'),
          course: checkAction('planModule.course.enable'),
          externalAppointment: checkAction('planModule.externalAppointment.enable'),
          amgVendingCheckoutRedirect: checkAction('admin.amgVendingScanConfig.page'),
          ticketBox: checkAction('admin.ticketBoxConfig.page'),
        },
      }
      return controls
    })

    const appointment = computed(() => {
      return [
        { key: 'appointmentOpen', label: '預約建立' },
        { key: 'appointmentChangeTime', label: '預約變更' },
        { key: 'appointmentCancel', label: '預約取消' },
        { key: 'appointmentTomorrow', label: '預約前提醒' },
        { key: 'appointmentEnd', label: '預約結束提醒' },
      ]
    })
    const wallet = computed(() => {
      const disabled = isTrustModuleEnabled.value
      const tooltip = '啟用藍新類信託服務無法關閉此通知'
      return [
        {
          key: 'walletDeposit',
          label: '儲值金儲值/收回',
          disabled,
          tooltip: disabled ? tooltip : '',
        },
        {
          key: 'walletUse',
          label: '儲值金使用/退款',
          disabled,
          tooltip: disabled ? tooltip : '',
        },
      ]
    })
    const deposit = computed(() => {
      return [
        { key: 'appointmentDepositRefund', label: '定金退款' },
      ]
    })
    const shopCashback = computed(() => {
      return [
        { key: 'shopCashbackAddPoint', label: '回饋金贈送' },
        { key: 'shopCashbackExpirationNotify', label: '回饋金即將到期' },
        { key: 'shopCashbackUsePoint', label: '回饋金扣除' },
        { key: 'shopCashbackCancelPoint', label: '回饋金回收' },
      ]
    })
    const coupon = computed(() => {
      return [
        { key: 'couponRecordOpen', label: '票券發放通知', event: goAdvanceCouponRecordNotification },
        { key: 'couponRecordVoid', label: '票券作廢通知' },
        { key: 'couponRecordExpireNotification', label: '票券即將到期通知', event: goAdvanceCouponRecordExpireNotification },
      ]
    })
    const ticketBox = computed(() => {
      return [
        { key: 'ticketBoxRecordOpen', label: '票匣發放通知', event: goAdvanceTicketBoxRecordOpen },
        { key: 'ticketBoxRecordUse', label: '票匣使用通知', event: goAdvanceTicketBoxRecordUse },
        { key: 'ticketBoxRecordVoidedNotification', label: '票匣回收通知', event: goAdvanceTicketBoxRecordVoided },
        { key: 'ticketBoxRecordExpireNotification', label: '票匣即將到期通知', event: goAdvanceTicketBoxRecordExpire },
      ]
    })
    const classTicket = computed(() => {
      const disabled = isTrustModuleEnabled.value
      const tooltip = '啟用藍新類信託服務無法關閉此通知'
      return [
        { key: 'classTicketOpen', label: '堂票建立' },
        {
          key: 'cancelClassTicket',
          label: '堂票作廢',
          disabled,
          tooltip: disabled ? tooltip : '',
        },
        {
          key: 'classTicketUse',
          label: '堂票使用',
          disabled,
          tooltip: disabled ? tooltip : '',
        },
        { key: 'cancelClassTicketUse', label: '堂票取消' },
        { key: 'classTicketExpireNotification', label: '堂票即將到期' },
        { key: 'classTicketGift', label: '堂票轉贈' },
      ]
    })
    const allianceActivity = computed(() => {
      return [
        { key: 'allianceActivityAddQualification', label: '聯盟行銷' },
      ]
    })
    const shopPoint = computed(() => {
      return [
        { key: 'shopPointAddPoint', label: '點數贈送' },
        { key: 'shopPointUsePoint', label: '點數扣除' },
        { key: 'shopPointCancelPoint', label: '點數回收' },
        { key: 'shopPointExpirationNotify', label: '點數即將到期' },
      ]
    })
    const memberLevel = computed(() => {
      return [
        { key: 'memberLevelUp', label: '會員達成升等' },
        { key: 'memberLevelDown', label: '會員達成降等' },
        { key: 'memberLevelRemain', label: '會員達成續等' },
        { key: 'memberLevelExpire', label: '會員等級即將到期' },
        { key: 'memberLevelVoidRecalculation', label: '會員等級重算結果' },
      ]
    })
    const memberInvite = computed(() => {
      return [
        { key: 'memberSuccessReferral', label: '會員推薦好友成功' },
      ]
    })
    const punchCard = computed(() => {
      return [
        { key: 'punchCardOpen', label: '次卡新增/作廢' },
        { key: 'punchCardUse', label: '次卡使用/補發' },
        { key: 'punchCardExpireNotification', label: '次卡到期' },
      ]
    })
    const pointCard = computed(() => {
      return [
        { key: 'pointCardOpen', label: '點數卡新增/作廢' },
        { key: 'pointCardUse', label: '點數卡使用/補發' },
        { key: 'pointCardExpireNotification', label: '點數卡到期' },
      ]
    })
    const periodicBenefit = computed(() => {
      return [
        { key: 'periodicBenefitRecordOpen', label: '會員權益新增' },
        { key: 'periodicBenefitRecordPause', label: '會員權益暫停' },
        { key: 'periodicBenefitRecordTerminate', label: '會員權益終止' },
      ]
    })
    const course = computed(() => {
      return [
        { key: 'courseOrderConfirm', label: '課程報名成功' },
        { key: 'courseOrderCancel', label: '課程報名取消' },
        { key: 'courseOrderChange', label: '課程報名資訊變更' },
        { key: 'courseSessionCancel', label: '課程報名停課' },
        { key: 'courseOrderRefundSuccess', label: '課程報名退款成功' },
        { key: 'courseOrderRefundFail', label: '課程報名退款失敗' },
      ]
    })

    const externalAppointment = computed(() => {
      return [
        { key: 'externalAppointmentOpen', label: '第三方預約建立' },
        { key: 'externalAppointmentUpdate', label: '第三方預約變更' },
        { key: 'externalAppointmentCancel', label: '第三方預約取消' },
        { key: 'externalAppointmentTomorrow', label: '第三方預約前提醒' },
      ]
    })

    const amgVendingCheckoutRedirect = computed(() => {
      return [
        { key: 'amgVendingCheckoutRedirect', label: '結帳完成後導購推播' },
      ]
    })

    const notifyList = computed(() => {
      return {
        appointment: appointment.value,
        wallet: wallet.value,
        deposit: deposit.value,
        shopCashback: shopCashback.value,
        coupon: coupon.value,
        ticketBox: ticketBox.value,
        classTicket: classTicket.value,
        allianceActivity: allianceActivity.value,
        shopPoint: shopPoint.value,
        memberLevel: memberLevel.value,
        memberInvite: memberInvite.value,
        punchCard: punchCard.value,
        pointCard: pointCard.value,
        periodicBenefit: periodicBenefit.value,
        course: course.value,
        externalAppointment: externalAppointment.value,
        amgVendingCheckoutRedirect: amgVendingCheckoutRedirect.value,
      }
    })
    return {
      isOhbotSuccessAdmin,
      getLineWebhookUrl,
      formData,
      settingData,
      onCopyWebhookUrl,
      lineWebhookUrl,
      notifyList,
      appointment,
      wallet,
      deposit,
      shopCashback,
      coupon,
      ticketBox,
      classTicket,
      allianceActivity,
      shopPoint,
      memberLevel,
      memberInvite,
      punchCard,
      pointCard,
      periodicBenefit,
      course,
      amgVendingCheckoutRedirect,
      showControl,
      goAdvanceCouponRecordNotification,
      goAdvanceCouponRecordExpireNotification,
      goParameters,
      externalAppointment,
      isTrustModuleEnabled,
    }
  },
  data: () => ({
    liff: null,
    showDialog: false,

    messageOption: {
      phoneVerifyHelp: false,
      appointmentOpen: false,
      appointmentCancel: false,
      appointmentChangeTime: false,
      appointmentTomorrow: false,
      appointmentEnd: false,
      walletDeposit: false,
      walletUse: false,
      classTicketOpen: false,
      classTicketUse: false,
      cancelClassTicket: false,
      cancelClassTicketUse: false,
      classTicketGift: false,
      allianceActivityAddQualification: false,
      couponRecordOpen: false,
      couponRecordVoid: false,
      appointmentDepositRefund: false,
      shopCashbackExpirationNotify: false,
      shopCashbackAddPoint: false,
      shopCashbackUsePoint: false,
      shopCashbackCancelPoint: false,
      classTicketExpireNotification: false,
      couponRecordExpireNotification: false,
      ticketBoxRecordOpen: false,
      ticketBoxRecordUse: false,
      ticketBoxRecordVoidedNotification: false,
      ticketBoxRecordExpireNotification: false,
      memberLevelUp: false,
      memberLevelDown: false,
      memberLevelRemain: false,
      memberLevelExpire: false,
      memberLevelVoidRecalculation: false,
      shopPointAddPoint: false,
      shopPointCancelPoint: false,
      shopPointUsePoint: false,
      shopPointExpirationNotify: false,
      memberSuccessReferral: false,
      punchCardOpen: false,
      punchCardUse: false,
      punchCardExpireNotification: false,
      pointCardOpen: false,
      pointCardUse: false,
      pointCardExpireNotification: false,
      periodicBenefitRecordOpen: false,
      periodicBenefitRecordPause: false,
      periodicBenefitRecordTerminate: false,
      courseOrderConfirm: false,
      courseOrderCancel: false,
      courseOrderChange: false,
      courseSessionCancel: false,
      courseOrderRefundSuccess: false,
      courseOrderRefundFail: false,
      externalAppointmentOpen: false,
      externalAppointmentUpdate: false,
      externalAppointmentTomorrow: false,
      externalAppointmentCancel: false,
      amgVendingCheckoutRedirect: false,
    },

    formRules: {
      lineId: noEmptyRules(),
      channelId: noEmptyRules(),
      channelSecret: noEmptyRules(),
      channelAccessToken: noEmptyRules(),
    },

  }),
  computed: {
    ...mapGetters(['shop', 'role', 'userFeatures', 'userPlanFeature']),
    useWallet () {
      return checkUserFeature(
        this.userPlanFeature,
        this.userFeatures,
        'admin.walletRecord.page',
      )
    },

    liffUrl () {
      if (!this.liffLink('appointment')) return false
      return this.liffLink('appointment').liffUrl
    },

    profileUrl () {
      if (!this.liffLink('appointment')) return false
      return `${this.liffLink('appointment').liffUrl}/profile`
    },
  },

  watch: {
    messageOption () {
    },
  },

  async mounted () {
    await Promise.allSettled([
      this.findLineConfig(),
      this.getLiff(),
      this.getLinePushConfig(),
      this.getLineWebhookUrl(),
    ])
  },

  methods: {
    copyLink (link) {
      if (!this.liffUrl) return
      copy(link)
      this.$message.success('已成功複製連結！')
    },

    //= > 檢查表單輸入驗證
    async checkForm () {
      return await formUtils.checkForm(this.$refs.form)
    },

    resetForm () {
      formUtils.resetForm(this.$refs.form)
      this.formData = {
        lineId: '',
        channelId: '',
        channelSecret: '',
        channelAccessToken: '',
      }
    },

    liffLink (name) {
      if (!this.liff) return false
      return this.liff.find((item) => item.name === name)
    },

    async findLineConfig () {
      try {
        const res = await FindLineConfig({ shopId: this.shop })
        this.formData = res
        this.settingData = res
      } catch (error) {
        if (error.includes('找不到')) {
          this.formData = {
            channelId: '',
            channelSecret: '',
            channelAccessToken: '',
          }
          this.settingData = ''
        }
      }
    },

    async updateLineConfig () {
      if (!(await this.checkForm())) return
      const form = this.formData
      try {
        await UpdateLineConfig({
          shopId: this.shop,
          lineId: form.lineId,
          channelId: form.channelId,
          channelSecret: form.channelSecret,
          channelAccessToken: form.channelAccessToken,
        })
        this.$message.success('更新成功')
        await this.findLineConfig()
        this.showDialog = false
      } catch (error) {
        this.$message.error({
          message: error || error.message,
        })
      }
    },

    async createLiff (name) {
      let pass = false
      if (this.formData.channelId !== '') pass = true
      if (this.formData.channelSecret !== '') pass = true
      if (this.formData.channelAccessToken !== '') pass = true
      if (!pass) {
        this.$message.warning('請先設置各項Channel參數')
        return
      }

      try {
        await CreateLiff({ shopId: this.shop, name })
        await this.getLiff()
        this.$message.success('更新 Liff 連結成功!')
      } catch (error) {
        console.log(error)
        this.$message.error(error)
      }
    },

    async getLiff () {
      try {
        const res = await GetLiff({ shopId: this.shop })
        this.liff = res
      } catch (error) {
        this.liff = []
      }
    },

    async getLinePushConfig () {
      try {
        const config = await GetLinePushConfig({ shopId: this.shop })
        Object.keys(this.messageOption).forEach((option) => {
          this.messageOption[option] = config[option]
        })

        if (this.isTrustModuleEnabled) {
          const forceEnableKeys = [
            'walletDeposit',
            'walletUse',
            'cancelClassTicket',
            'classTicketUse',
          ]
          forceEnableKeys.forEach((key) => {
            this.messageOption[key] = true
          })
        }
      } catch (error) {
        console.log(error)
        this.$message.error(error)
      }
    },

    async updateLinePushGroup (val, options) {
      options.forEach((name) => {
        this.messageOption[name] = val
      })
      await this.updateLinePushConfig()
    },

    async updateLinePushConfig () {
      try {
        await UpdateLinePushConfig({
          shopId: this.shop,
          ...this.messageOption,
        })
        this.$message.success('已更新通知設定 !')
      } catch (error) {
        console.log(error)
        this.$message.error(error)
      }
    },
  },
})
</script>

<style scoped lang="scss">
section {
  margin-top: 2rem;
  margin-bottom: 50px;
}

.title {
  font-size: 20px;
  margin-right: 10px;
}

.link-card {
  margin-bottom: 20px;
}

.secret-text {
  width: 100px;
  font-weight: 400;
  @apply text-gray-80;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.copy-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  background: white;
  overflow: hidden;
  max-width: 440px;
  height: 44px;
  border-radius: 4px;

  a {
    font-weight: 400;
    @apply text-gray-80;
    text-decoration: none;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding: 10px 20px;
  }

  .copy-btn {
    @apply bg-primary-100 text-white cursor-pointer;
    white-space: nowrap;
    padding: 8px 50px;
    &:hover,
    &:focus,
    &:active {
      // background-color: darken($color: $primary-1, $amount: 5);
    }
  }

  .disable {
    @apply bg-gray-40 cursor-not-allowed;
    &:hover,
    &:focus,
    &:active {
      @apply bg-gray-40;
    }
  }
}

.disable {
  @apply bg-gray-40 cursor-not-allowed border border-gray-40;
  &:hover,
  &:focus,
  &:active {
    @apply bg-gray-40 border border-gray-40;
  }
}

.dialog-input {
  width: 340px;
}

.btn {
  width: 140px;
}

::v-deep .el-form-item__label {
  @apply text-normal font-medium;
}
::v-deep .advance-btn {
  @apply text-sub pl-[48px];
}
</style>
